<template>
  <div class="campaigns-page">
    <div class="content-area">
      <div class="page-header">
        <div>
          <h1 class="page-title">
            Outbound Campaigns
          </h1>
          <p class="page-subtitle">
            Upload a list of numbers and let an agent call them on a schedule.
          </p>
        </div>
        <v-btn
          v-if="canWrite"
          color="primary"
          class="mank-btn"
          elevation="0"
          @click="drawerOpen = true">
          <Icon name="plus" variant="solid" color="onPrimary"/>
          New campaign
        </v-btn>
      </div>

      <div class="main-content">
        <div class="toolbar">
          <MankSelect
            v-model="statusFilter"
            :items="statusFilterItems"
            item-text="text"
            item-value="value"
            dense
            hide-details
            class="status-filter"
            :menu-props="{ offsetY: true, nudgeBottom: 8 }"/>
        </div>

        <div v-if="!loading && !campaigns.length" class="empty-state">
          <v-icon size="72" color="grey lighten-1">
            mdi-phone-outgoing-outline
          </v-icon>
          <h3 class="ds-panel-title mt-4 mb-1">
            {{ statusFilter ? 'No campaigns with this status' : 'No campaigns yet' }}
          </h3>
          <p class="text-body-3 text--secondary mb-4">
            A campaign calls a list of phone numbers with an agent, a few numbers at a time.
          </p>
          <v-btn
            v-if="canWrite && !statusFilter"
            color="primary"
            class="mank-btn"
            elevation="0"
            @click="drawerOpen = true">
            <Icon name="plus" variant="solid" color="onPrimary"/>
            New campaign
          </v-btn>
        </div>

        <div v-else class="table-wrap">
          <v-data-table
            :headers="headers"
            :items="campaigns"
            :options.sync="tableOptions"
            :server-items-length="total"
            :loading="loading"
            :footer-props="{ itemsPerPageOptions: [10, 20, 50, 100] }"
            item-key="id"
            class="campaign-table"
            loading-text="Loading campaigns…"
            @click:row="openCampaign">
            <template #item.name="{ item }">
              <div class="name-cell">
                <span class="campaign-name">{{ item.name }}</span>
                <span class="text-caption text--secondary">{{ agentName(item.agent_id) }}</span>
              </div>
            </template>
            <template #item.status="{ item }">
              <v-chip small :color="statusOf(item).color" text-color="white">
                {{ statusOf(item).label }}
              </v-chip>
              <v-tooltip v-if="item.last_error && item.status === 'paused'" bottom max-width="360">
                <template #activator="{ on, attrs }">
                  <v-icon small color="warning" class="ml-1" v-bind="attrs" v-on="on">
                    mdi-alert-circle-outline
                  </v-icon>
                </template>
                <span>{{ item.last_error }}</span>
              </v-tooltip>
            </template>
            <template #item.progress="{ item }">
              <div class="progress-cell">
                <v-progress-linear
                  :value="progressOf(item)"
                  height="6"
                  rounded
                  color="success"
                  background-color="grey lighten-3"/>
                <span class="text-caption text--secondary">
                  {{ finishedOf(item) }} / {{ item.total_numbers }}
                  · {{ item.number_counts.called }} called
                  <template v-if="item.number_counts.failed">· {{ item.number_counts.failed }} failed</template>
                </span>
              </div>
            </template>
            <template #item.pacing="{ item }">
              <span class="text-body-3">{{ item.batch_size }} every {{ formatSeconds(item.interval_seconds) }}</span>
            </template>
            <template #item.next_run_at="{ item }">
              <span class="text-body-3">{{ item.status === 'active' ? nextRunLabel(item) : '—' }}</span>
            </template>
            <template #item.actions="{ item }">
              <div class="row-actions" @click.stop>
                <v-btn
                  v-if="canWrite && canStart(item)"
                  icon
                  small
                  :loading="busyId === item.id"
                  aria-label="Start campaign"
                  @click="runAction(item, 'start')">
                  <v-icon small color="success">
                    mdi-play
                  </v-icon>
                </v-btn>
                <v-btn
                  v-if="canWrite && item.status === 'active'"
                  icon
                  small
                  :loading="busyId === item.id"
                  aria-label="Pause campaign"
                  @click="runAction(item, 'pause')">
                  <v-icon small color="warning">
                    mdi-pause
                  </v-icon>
                </v-btn>
                <v-btn icon small aria-label="Open campaign" @click="openCampaign(item)">
                  <v-icon small>
                    mdi-chevron-right
                  </v-icon>
                </v-btn>
              </div>
            </template>
          </v-data-table>
        </div>
      </div>
    </div>

    <CreateOutboundCampaignDrawer
      :visible="drawerOpen"
      @close="drawerOpen = false"
      @created="onCreated"/>
  </div>
</template>

<script>
import CreateOutboundCampaignDrawer from '@/components/CreateOutboundCampaignDrawer.vue';
import Icon from '@/components/Icon.vue';
import MankSelect from '@/components/MankSelect.vue';
import AgentService from '@/services/AgentService';
import OutboundCampaignService from '@/services/OutboundCampaignService';
import {
  CAMPAIGN_REFRESH_MS,
  CAMPAIGN_STATUS,
  campaignStatus,
  finishedCount,
  formatDateTime,
  formatSeconds,
  notifyError,
  notifySuccess,
  progressPercent,
} from '@/constants/outboundCampaign';

export default {
  name: 'OutboundCampaigns',
  components: {
    CreateOutboundCampaignDrawer,
    Icon,
    MankSelect,
  },
  data() {
    return {
      campaigns: [],
      total: 0,
      loading: true,
      agents: {},
      statusFilter: '',
      tableOptions: { page: 1, itemsPerPage: 20 },
      drawerOpen: false,
      busyId: null,
      refreshTimer: null,
      headers: [
        { text: 'Campaign', value: 'name', sortable: false },
        { text: 'Status', value: 'status', sortable: false, width: '150px' },
        { text: 'Progress', value: 'progress', sortable: false, width: '260px' },
        { text: 'Pacing', value: 'pacing', sortable: false },
        { text: 'Next batch', value: 'next_run_at', sortable: false },
        { text: '', value: 'actions', sortable: false, align: 'end', width: '120px' },
      ],
    };
  },
  computed: {
    canWrite() {
      return this.$store.getters['auth/hasPermission']('write:outbound-campaign');
    },
    statusFilterItems() {
      return [
        { text: 'All statuses', value: '' },
        ...Object.entries(CAMPAIGN_STATUS).map(([value, s]) => ({ text: s.label, value })),
      ];
    },
    hasActive() {
      return this.campaigns.some((c) => c.status === 'active');
    },
  },
  watch: {
    // The table only exists once there are campaigns, so the first load happens in mounted.
    tableOptions(next, prev) {
      if (next.page !== prev.page || next.itemsPerPage !== prev.itemsPerPage) this.load();
    },
    statusFilter() {
      if (this.tableOptions.page !== 1) {
        this.tableOptions = { ...this.tableOptions, page: 1 }; // the options watcher reloads
      } else {
        this.load();
      }
    },
  },
  mounted() {
    this.load();
    this.loadAgents();
    this.refreshTimer = setInterval(() => {
      if (this.hasActive && !document.hidden) this.load({ silent: true });
    }, CAMPAIGN_REFRESH_MS);
  },
  beforeDestroy() {
    clearInterval(this.refreshTimer);
  },
  methods: {
    formatSeconds,
    statusOf(item) {
      return campaignStatus(item.status);
    },
    progressOf(item) {
      return progressPercent(item);
    },
    finishedOf(item) {
      return finishedCount(item);
    },
    agentName(agentId) {
      return this.agents[agentId] || agentId;
    },
    canStart(item) {
      return ['draft', 'paused'].includes(item.status) && item.number_counts.pending > 0;
    },
    nextRunLabel(item) {
      if (item.start_at && new Date(item.start_at) > new Date()) return `Starts ${formatDateTime(item.start_at)}`;
      if (!item.next_run_at || new Date(item.next_run_at) <= new Date()) return 'Now';
      return formatDateTime(item.next_run_at);
    },
    async loadAgents() {
      try {
        const response = await AgentService.getAllAgents();
        const list = response?.data || response || [];
        const map = {};
        (Array.isArray(list) ? list : []).forEach((a) => { map[a.id] = a.name; });
        this.agents = map;
      } catch (e) {
        // Agent names fall back to ids.
      }
    },
    async load(options = {}) {
      if (!options.silent) this.loading = true;
      try {
        const { page, itemsPerPage } = this.tableOptions;
        const params = { page, page_size: itemsPerPage > 0 ? itemsPerPage : 100 };
        if (this.statusFilter) params.status = this.statusFilter;
        const { items, pagination } = await OutboundCampaignService.list(params, options);
        this.campaigns = items;
        this.total = pagination?.total ?? items.length;
      } catch (err) {
        if (!options.silent) notifyError(this.$store, err, 'Could not load campaigns.');
      } finally {
        this.loading = false;
      }
    },
    openCampaign(item) {
      this.$router.push({ name: 'OutboundCampaignDetail', params: { id: item.id } });
    },
    async runAction(item, action) {
      this.busyId = item.id;
      try {
        await OutboundCampaignService.action(item.id, action);
        notifySuccess(this.$store, action === 'start' ? 'Campaign started.' : 'Campaign paused.');
        await this.load({ silent: true });
      } catch (err) {
        notifyError(this.$store, err, `Could not ${action} the campaign.`);
      } finally {
        this.busyId = null;
      }
    },
    onCreated({ campaignId, result }) {
      this.drawerOpen = false;
      this.$router.push({
        name: 'OutboundCampaignDetail',
        params: { id: campaignId, initialResult: result },
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/styles/colors.scss';
@import '@/styles/variables.scss';

.campaigns-page {
  height: 100%;
}

.page-subtitle {
  font-size: $body-3-size;
  color: $light-foreground-3;
  margin: 4px 0 0;
}

.main-content {
  padding: 24px;
}

.toolbar {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 16px;
}

.status-filter {
  max-width: 220px;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 64px 24px;
}

.table-wrap {
  border: 1px solid $light-border;
  border-radius: 12px;
  overflow: hidden;
  background: $light-surface-2;
}

.campaign-table {
  ::v-deep tbody tr {
    cursor: pointer;
  }

  ::v-deep .v-data-table-header th {
    color: $light-foreground-3 !important;
    font-weight: 600;
    background-color: $light-surface-1;
  }
}

.name-cell {
  display: flex;
  flex-direction: column;
  padding: 8px 0;
}

.campaign-name {
  font-weight: 600;
  color: $light-foreground-1;
}

.progress-cell {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 8px 0;
}

.row-actions {
  display: inline-flex;
  gap: 2px;
}
</style>
